import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface GoldilocksZoneProps {
  width?: number;
  height?: number;
}

export const GoldilocksZone: React.FC<GoldilocksZoneProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);
  const [distance, setDistance] = useState(50); // 0 to 100
  const [status, setStatus] = useState<'HOT' | 'GOLD' | 'COLD'>('GOLD');

  useEffect(() => {
    if (distance < 35) setStatus('HOT');
    else if (distance > 70) setStatus('COLD');
    else setStatus('GOLD');
  }, [distance]);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerY = size / 2;
    
    // Draw Zones
    const trackWidth = size - 60;
    const startX = 40;
    
    const zoneHeight = 40;
    const yTop = centerY - zoneHeight / 2;

    // Hot zone (0 to 35%)
    ctx.fillStyle = 'rgba(255, 68, 0, 0.15)';
    ctx.fillRect(startX, yTop, trackWidth * 0.35, zoneHeight);
    
    // Goldilocks zone (35% to 70%)
    ctx.fillStyle = 'rgba(74, 222, 128, 0.15)';
    ctx.fillRect(startX + trackWidth * 0.35, yTop, trackWidth * 0.35, zoneHeight);

    // Cold zone (70% to 100%)
    ctx.fillStyle = 'rgba(136, 187, 255, 0.15)';
    ctx.fillRect(startX + trackWidth * 0.70, yTop, trackWidth * 0.30, zoneHeight);

    // Draw Star
    ctx.save();
    const sunGrad = ctx.createRadialGradient(20, centerY, 5, 20, centerY, 60);
    sunGrad.addColorStop(0, '#FFFFFF');
    sunGrad.addColorStop(0.2, '#FDE047');
    sunGrad.addColorStop(0.5, '#F97316');
    sunGrad.addColorStop(1, 'rgba(249, 115, 22, 0)');
    ctx.fillStyle = sunGrad;
    ctx.beginPath();
    ctx.arc(20, centerY, 60, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Calculate planet position
    const pX = startX + (distance / 100) * trackWidth;
    
    // Draw Planet based on status
    ctx.save();
    ctx.translate(pX, centerY);

    if (distance < 35) {
      // TOO HOT
      ctx.fillStyle = '#EA580C';
      ctx.beginPath();
      ctx.arc(0, 0, 12, 0, Math.PI * 2);
      ctx.fill();

      // Wavy heat lines
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.lineWidth = 1.5;
      for (let i = -1; i <= 1; i++) {
        const offset = Math.sin(frameCount * 0.2 + i) * 2;
        ctx.beginPath();
        ctx.moveTo(i * 6, -15);
        ctx.bezierCurveTo(i * 6 + offset, -20, i * 6 - offset, -25, i * 6, -30);
        ctx.stroke();
      }
    } else if (distance > 70) {
      // TOO COLD
      ctx.fillStyle = '#CBD5E1';
      ctx.beginPath();
      ctx.arc(0, 0, 12, 0, Math.PI * 2);
      ctx.fill();

      // Frost effect (little jagged lines on surface)
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 1;
      for(let i=0; i<3; i++) {
        ctx.beginPath();
        ctx.moveTo(-5 + i*5, -5 + (i%2)*10);
        ctx.lineTo(-2 + i*5, 0 + (i%2)*10);
        ctx.lineTo(2 + i*5, -3 + (i%2)*10);
        ctx.stroke();
      }
    } else {
      // GOLDILOCKS
      ctx.fillStyle = '#3B82F6'; // Ocean blue
      ctx.beginPath();
      ctx.arc(0, 0, 12, 0, Math.PI * 2);
      ctx.fill();

      // Water shimmer
      ctx.fillStyle = '#22C55E'; // Green landmass
      ctx.beginPath();
      ctx.arc(-4, -2, 5, 0, Math.PI*2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(4, 3, 4, 0, Math.PI*2);
      ctx.fill();
      
      ctx.strokeStyle = `rgba(255,255,255,${Math.sin(frameCount * 0.1) * 0.3 + 0.3})`;
      ctx.beginPath();
      ctx.moveTo(-8, Math.sin(frameCount * 0.1)*2);
      ctx.lineTo(8, Math.sin(frameCount * 0.1)*2);
      ctx.stroke();
    }
    
    ctx.restore();

  }, [size, distance]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size - 80, position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #1E293B' }}>
        <canvas ref={canvasRef} />
      </div>
      
      <div style={{ marginTop: '20px', width: '100%', maxWidth: '300px', textAlign: 'center' }}>
        <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px', color: '#94A3B8' }}>
          <span>Distance from Star</span>
        </label>
        <input 
          type="range" 
          min="0" 
          max="100" 
          value={distance} 
          onChange={(e) => setDistance(Number(e.target.value))}
          style={{ width: '100%', cursor: 'pointer' }}
        />
        <div style={{ 
          marginTop: '12px', 
          fontSize: '15px', 
          fontWeight: 'bold', 
          height: '24px', 
          color: status === 'GOLD' ? '#4ADE80' : status === 'HOT' ? '#F87171' : '#60A5FA' 
        }}>
          {status === 'GOLD' && 'Liquid Oceans 💧'}
          {status === 'HOT' && 'Water Boils Away ♨️'}
          {status === 'COLD' && 'Water Freezes ❄️'}
        </div>
      </div>
    </div>
  );
};
